import React, { useState, useMemo } from 'react';
import { TabState, EphemeralDataSet, DerivedDataSet, DataValue } from '../../../../types';
import { DataTableView } from '../../DataTableView';
import { useProjectStore } from '../../../stores/projectStore';
import { useNotificationStore } from '../../../stores/notificationStore';
import { convertEphemeralToDerivedDataset } from '../../../utils/convertEphemeralToDerivedDataset';
import { FilterExpressionParser } from '../../../utils/FilterExpressionParser';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import { TextField } from '../../common/TextField';
import * as styles from '../../../tab-system/TabContainer.css.ts';

interface FilteredDataTabContentProps {
  tab: TabState;
}

export const FilteredDataTabContent: React.FC<FilteredDataTabContentProps> = ({ tab }) => {
  const { error: showError } = useNotificationStore();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [datasetName, setDatasetName] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [filterError, setFilterError] = useState<string | null>(null);

  // tab.metadataから直接EphemeralDataSetを取得
  const ephemeralDataset = tab.metadata?.ephemeralDataset as EphemeralDataSet | undefined;

  // filterExpression付きかチェック
  const hasFilterExpression = ephemeralDataset?.metadata?.filterExpression != null;

  // フィルタを適用したデータセット
  const filteredDataset = useMemo<EphemeralDataSet | null>(() => {
    if (!ephemeralDataset || !filterInput.trim()) {
      setFilterError(null);
      return ephemeralDataset || null;
    }

    try {
      // フィルタ式をパース
      const parser = new FilterExpressionParser();
      const parseResult = parser.parse(filterInput);

      if (!parseResult.success) {
        setFilterError(parseResult.error || 'Invalid filter expression');
        return ephemeralDataset;
      }

      const filterExpression = parseResult.expression!;

      // フィルタ適用
      const filteredIndices: number[] = [];
      const rowCount = ephemeralDataset.rowCount || 0;

      for (let i = 0; i < rowCount; i++) {
        const rowData: Record<string, DataValue> = {};
        ephemeralDataset.schema.columns.forEach(col => {
          const columnData = ephemeralDataset.data?.[col.id];
          rowData[col.name] = columnData?.[i];
        });

        if (FilterExpressionParser.evaluate(filterExpression, rowData)) {
          filteredIndices.push(i);
        }
      }

      // フィルタ済みデータを構築
      const filteredData: Record<string, DataValue[]> = {};
      ephemeralDataset.schema.columns.forEach(col => {
        const sourceData = ephemeralDataset.data?.[col.id] || [];
        filteredData[col.id] = filteredIndices.map(i => sourceData[i]);
      });

      setFilterError(null);
      return {
        ...ephemeralDataset,
        data: filteredData,
        rowCount: filteredIndices.length,
        metadata: {
          ...ephemeralDataset.metadata,
          additionalFilterExpression: filterExpression
        }
      };
    } catch (error) {
      setFilterError(error instanceof Error ? error.message : 'Invalid filter expression');
      return ephemeralDataset;
    }
  }, [ephemeralDataset, filterInput]);

  // Save as Datasetボタンのハンドラ
  const handleSaveAsDataset = () => {
    if (!ephemeralDataset || !hasFilterExpression) return;

    // デフォルト名を設定
    setDatasetName(ephemeralDataset.metadata?.description || ephemeralDataset.name || 'Filtered Data');
    setShowSaveDialog(true);
  };

  // 保存実行
  const handleSave = () => {
    if (!filteredDataset || !datasetName.trim()) return;

    try {
      // フィルタ適用後のEphemeralDataSetをDerivedDataSetに変換
      const derivedDataset: DerivedDataSet = convertEphemeralToDerivedDataset(
        filteredDataset,
        datasetName.trim()
      );

      // プロジェクトに追加
      useProjectStore.getState().addDataset(derivedDataset);

      // ダイアログを閉じる
      setShowSaveDialog(false);
      setDatasetName('');
    } catch (error) {
      console.error('Failed to save dataset:', error);
      showError(`Failed to save dataset: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // フィルタクリア
  const handleClearFilter = () => {
    setFilterInput('');
    setFilterError(null);
  };

  // EphemeralDataSetが存在しない場合
  if (!ephemeralDataset) {
    return (
      <div data-testid="filtered-data-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <p>No ephemeral dataset found in tab metadata.</p>
        </div>
      </div>
    );
  }

  // filterExpressionが無い場合
  if (!hasFilterExpression) {
    return (
      <div data-testid="filtered-data-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <p>This ephemeral dataset does not have filter expression metadata.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div data-testid="filtered-data-content" className={styles.tabContentScrollable} style={{ padding: 0 }}>
        {/* フィルタコンテナ */}
        <div className={styles.filterContainer}>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Enter filter expression (e.g., Sales > 200)"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            data-testid="filter-input"
          />
          {filterInput && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClearFilter}
              data-testid="clear-filter-button"
            >
              Clear
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveAsDataset}
            data-testid="save-as-dataset-button"
          >
            Save as Dataset
          </Button>
        </div>

        {/* フィルタエラー表示 */}
        {filterError && (
          <div style={{ padding: '8px 16px', backgroundColor: '#fee', color: '#c00', fontSize: '14px' }}>
            {filterError}
          </div>
        )}

        {/* 行数表示 */}
        {filteredDataset && ephemeralDataset && filteredDataset.rowCount !== ephemeralDataset.rowCount && (
          <div style={{ padding: '8px 16px', backgroundColor: '#e3f2fd', color: '#1976d2', fontSize: '14px' }}>
            Showing {filteredDataset.rowCount} of {ephemeralDataset.rowCount} rows (filtered)
          </div>
        )}

        {/* データテーブル表示 */}
        <DataTableView dataset={filteredDataset || ephemeralDataset} />
      </div>

      {/* 保存ダイアログ */}
      <Dialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save as Dataset"
        width="md"
        data-testid="save-dialog"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowSaveDialog(false)}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!datasetName.trim()}
              data-testid="save-button"
            >
              Save
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextField
            label="Dataset Name"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            placeholder="Enter dataset name"
            autoFocus
            fullWidth
            data-testid="dataset-name-input"
          />

          {/* フィルタ適用のメッセージ */}
          {filteredDataset && ephemeralDataset && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#1976d2'
            }}>
              {filterInput ? (
                <>
                  <strong>Additional filter will be applied:</strong>
                  <div style={{ marginTop: '4px' }}>"{filterInput}"</div>
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    Saving {filteredDataset.rowCount} rows (filtered from {ephemeralDataset.rowCount} rows)
                  </div>
                </>
              ) : (
                <>
                  Saving {filteredDataset.rowCount} rows
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};
